import React, { useState, useMemo } from "react";
import PErrors from "../../../ui/input/PErrors";
import PTextArea from "../../../ui/input/PTextArea";
import PButton from "../../../ui/input/PButton";

interface ComposeMessageScreenProps {
  recipientCount: number;
  onNext: (message: string) => void;
  onBack: () => void;
}

const SPECIAL_SEQUENCES = {
  "{name}": "Recipient's first name",
  "{event_name}": "Event name",
  "{code}": "Event entry code",
  "{download_link}": "App store download link",
  "{referral_link}": "Referral link including the event code",
};

const MAX_MESSAGE_LENGTH = 240;

const ComposeMessageScreen: React.FC<ComposeMessageScreenProps> = ({
  recipientCount,
  onNext,
  onBack,
}) => {
  const [message, setMessage] = useState(
    "Hey!\n\nIt’s Dini, your matchmaker at Sitch. You could be getting set up at today's event, but only if you tell me who you’re into first. Use code {code} when you download the app: {download_link}",
  );
  const [errors, setErrors] = useState<string[]>([]);

  // Validate message for invalid curly bracket sequences
  const validateMessage = (text: string): string[] => {
    const validationErrors: string[] = [];

    // Check for curly brackets and validate they're allowed sequences
    const bracketPattern = /\{[^}]*\}/g;
    const matches = text.match(bracketPattern) || [];

    matches.forEach((match) => {
      if (!Object.keys(SPECIAL_SEQUENCES).includes(match)) {
        validationErrors.push(
          `Invalid placeholder "${match}". Valid placeholders are: ${Object.keys(
            SPECIAL_SEQUENCES,
          ).join(", ")}`,
        );
      }
    });

    // Check for unclosed brackets
    const openBrackets = (text.match(/\{/g) || []).length;
    const closeBrackets = (text.match(/\}/g) || []).length;
    if (openBrackets !== closeBrackets) {
      validationErrors.push("Unclosed curly bracket detected");
    }

    return validationErrors;
  };

  const handleMessageChange = (text: string) => {
    if (text.length <= MAX_MESSAGE_LENGTH) {
      setMessage(text);
      const validationErrors = validateMessage(text);
      setErrors(validationErrors);
    }
  };

  const handleNext = () => {
    const validationErrors = validateMessage(message);
    if (validationErrors.length === 0 && message.trim()) {
      onNext(message);
    } else if (!message.trim()) {
      setErrors(["Message cannot be empty"]);
    }
  };

  const remainingChars = MAX_MESSAGE_LENGTH - message.length;
  const isValid = errors.length === 0 && message.trim().length > 0;

  // Example message preview with replacements
  const messagePreview = useMemo(() => {
    let preview = message;
    preview = preview.replace(/\{name\}/g, "John");
    preview = preview.replace(/\{event_name\}/g, "Summer Mixer");
    preview = preview.replace(/\{code\}/g, "ABC123");
    preview = preview.replace(
      /\{download_link\}/g,
      "https://download.joinsitch.com/",
    );
    preview = preview.replace(
      /\{referral_link\}/g,
      "https://joinsitch.com/referral/ABC123/",
    );
    return preview;
  }, [message]);

  return (
    <div className="flex flex-col h-full bg-black text-white text-left w-screen max-w-3xl">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/20 flex flex-row items-center justify-between">
        <h2 className="text-lg font-semibold">Compose Message</h2>
        <p className="text-sm text-white/90 mt-1">
          {recipientCount} recipient
          {recipientCount !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Message Composition */}
      <div className="flex-1 px-6 py-4 space-y-4">
        {/* Message Input */}
        <div>
          <div className="text-black">
            <PTextArea
              value={message}
              onChange={(e) => handleMessageChange(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
            />
          </div>

          <div className="mt-1 flex justify-between items-center">
            <span
              className={`text-sm ${
                remainingChars < 20 ? "text-red-600" : "text-white"
              }`}
            >
              {remainingChars} characters remaining
            </span>
          </div>
        </div>

        {/* Available Placeholders */}
        <div>
          <div className="bg-white/20 rounded-md p-3 space-y-1">
            {Object.entries(SPECIAL_SEQUENCES).map(([key, description]) => (
              <div key={key} className="flex items-center text-sm">
                <code className="bg-black px-2 py-1 rounded font-mono text-xs mr-2">
                  {key}
                </code>
                <span>→ {description}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Message Preview */}
        {message && (
          <div>
            <h3 className="text-sm font-medium mb-2">
              Preview (example values)
            </h3>
            <div className="bg-blue-50 rounded-md p-3 text-sm text-blue-900 border border-blue-200 whitespace-pre-line">
              {messagePreview}
            </div>
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && <PErrors errors={errors} />}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/20">
        <div className="flex gap-3">
          <PButton
            type="button"
            kind="secondary"
            className="w-full"
            onClick={onBack}
          >
            Back
          </PButton>
          <PButton
            type="button"
            kind="primary"
            className="w-full"
            onClick={handleNext}
            disabled={!isValid}
          >
            Next
          </PButton>
        </div>
      </div>
    </div>
  );
};

export default ComposeMessageScreen;
